import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type ChartTheme = 'light' | 'dark';

export interface ChartColors {
  axis: string;
  grid: string;
  background: string;
  text: string;
  tooltipBg: string;
  tooltipBorder: string;
}

const LIGHT_COLORS: ChartColors = {
  axis: '#888888',
  grid: '#ccc',
  background: '#fff',
  text: '#333',
  tooltipBg: '#fff',
  tooltipBorder: '#ccc',
};

const DARK_COLORS: ChartColors = {
  axis: '#888888',
  grid: '#444',
  background: '#333',
  text: '#fff',
  tooltipBg: '#333',
  tooltipBorder: '#555',
};

const STORAGE_KEY = 'checkfirst-chart-theme';

interface ChartThemeContextType {
  chartTheme: ChartTheme;
  setChartTheme: (theme: ChartTheme) => void;
  colors: ChartColors;
}

const ChartThemeContext = createContext<ChartThemeContextType | undefined>(undefined);

interface ChartThemeProviderProps {
  children: ReactNode;
}

export function ChartThemeProvider({ children }: ChartThemeProviderProps) {
  const [chartTheme, setChartThemeState] = useState<ChartTheme>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      return (saved as ChartTheme) || 'light';
    }
    return 'light';
  });

  const colors = chartTheme === 'light' ? LIGHT_COLORS : DARK_COLORS;

  const setChartTheme = useCallback((theme: ChartTheme) => {
    setChartThemeState(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, []);

  return React.createElement(
    ChartThemeContext.Provider,
    { value: { chartTheme, setChartTheme, colors } },
    children
  );
}

export function useChartTheme() {
  const context = useContext(ChartThemeContext);
  if (!context) {
    throw new Error('useChartTheme must be used within ChartThemeProvider');
  }
  return context;
}

/**
 * Hook to get chart colors based on the current chart theme.
 * This enables light/dark mode support for Recharts components.
 */
export function useChartColors(): ChartColors & { 
  cardClassName: string;
  tooltipStyle: React.CSSProperties;
} {
  const context = useContext(ChartThemeContext);
  
  // If no provider, return light colors as default
  if (!context) {
    return { 
      ...LIGHT_COLORS,
      cardClassName: 'bg-white border-gray-200 text-gray-900',
      tooltipStyle: {
        backgroundColor: LIGHT_COLORS.tooltipBg,
        borderColor: LIGHT_COLORS.tooltipBorder,
        color: LIGHT_COLORS.text,
      }
    };
  }
  
  const cardClassName = context.chartTheme === 'light' 
    ? 'bg-white border-gray-200 text-gray-900' 
    : 'bg-gray-800 border-gray-700 text-gray-100';
  
  const tooltipStyle: React.CSSProperties = {
    backgroundColor: context.colors.tooltipBg,
    borderColor: context.colors.tooltipBorder,
    color: context.colors.text,
  };
  
  return { ...context.colors, cardClassName, tooltipStyle };
}

// Static chart colors for components that don't need reactivity
// These are the light mode defaults (analytics pages use white bg)
export const CHART_COLORS = LIGHT_COLORS;
